import { Button, Comments, Modal } from "@corecrm/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../api/client";
import { useTasks } from "../api/queries";
import type { CommentBoard, Task } from "../api/types";
import { QuestionDialogById } from "./Questions";

/* Comments in the portal: the threads the team shared with you, and yours (always shared). A
   thread on a task links to it; the task opens with its own comments. */

const unreadKey = (projectId: string) => ["projects", projectId, "comments", "unread"];

/** The Comments tab's counter: how many comments you haven't read. */
export function useUnreadComments(projectId: string) {
  return useQuery({
    queryKey: unreadKey(projectId),
    queryFn: async () => (await api.get<CommentBoard>(`/projects/${projectId}/comments`)).unread_count,
  });
}

export function ProjectComments({ projectId, highlight }: { projectId: string; highlight?: string | null }) {
  const qc = useQueryClient();
  const tasks = useTasks(projectId);
  const [opened, setOpened] = useState<string | null>(null);
  const [question, setQuestion] = useState<string | null>(null);
  const task = tasks.data?.find((t) => t.id === opened);
  return (
    <>
      <Comments
        api={api}
        base={`/projects/${projectId}/comments`}
        onOpenTask={setOpened}
        onOpenQuestion={setQuestion}
        onUnread={(n) => qc.setQueryData(unreadKey(projectId), n)}
        highlight={highlight}
      />
      {task && <TaskComments projectId={projectId} task={task} onClose={() => setOpened(null)} />}
      {question && <QuestionDialogById id={question} onClose={() => setQuestion(null)} />}
    </>
  );
}

/** A shared task, with its comments. */
export function TaskComments({
  projectId,
  task,
  onClose,
}: {
  projectId: string;
  task: Task;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  return (
    <Modal wide title={task.title} onClose={onClose} footer={<Button onClick={onClose}>Close</Button>}>
      <div className="stack">
        {task.description && <p className="task-desc">{task.description}</p>}
        {task.acceptance_criteria && (
          <div>
            <strong>Acceptance criteria</strong>
            <p className="task-desc">{task.acceptance_criteria}</p>
          </div>
        )}
        <Comments
          api={api}
          base={`/projects/${projectId}/comments`}
          taskId={task.id}
          onUnread={() => void qc.invalidateQueries({ queryKey: unreadKey(projectId) })}
        />
      </div>
    </Modal>
  );
}
